import classNames from "classnames";
import React, { useEffect, useRef } from "react";

import { toggleModal } from "../../slices/popupSlice";
import { trackInteractionEvent } from "../../tracking/googleAnalytics";
import { useDispatch, useSelector } from "../../utils/hooks";
import strings from "../../utils/strings";
import Alert from "../common/Alert";
import Footer from "../common/Footer";
import Header from "../common/Header";
import Loading from "../common/Loading";
import { Spacer } from "../common/Spacer";
import { ProductsList } from "../list/ProductsList";
import { MODAL_TYPES } from "../modals/GeneralModal.types";
import BoughtProduct from "../products/BoughtProduct";
import ConfigServices from "../products/ConfigServices";
import { INSURANCE_VARIANT } from "../products/Insurance.type";
import SubscriptionDetails from "../products/Subscription/SubscriptionDetails";
import SubscriptionHighlight from "../products/Subscription/SubscriptionHighlight";
import SubscriptionInfo from "../products/Subscription/SubscriptionInfo";
import SubscriptionReadMore from "../products/Subscription/SubscriptionReadMore";

import Configuration from "./Configuration";

import "./Popup.scss";
interface PopupPropsI {
	closePopup: () => void;
}

export const DrawerContent = (props: PopupPropsI) => {
	const dispatch = useDispatch();

	const drawerRef = useRef(null);
	const isFetching = useSelector((state) => state.popup.isFetching);
	const isError = useSelector((state) => state.popup.error.isError);

	const isBoughtWithSubscription = useSelector(
		(state) =>
			state.popup.products.mainProduct.subscription?.isBoughtWithSubscription,
	);

	const showSubscriptionDataOnly = useSelector(
		(state) => state.popup.showSubscriptionDataOnly,
	);

	const insurance = useSelector(
		(state) => state.popup.products.mainProduct.insurance,
	);

	const insuranceModalType = insurance.some(
		(item) => item.variant === INSURANCE_VARIANT.SCREEN,
	)
		? MODAL_TYPES.SCREEN_INSURANCE
		: MODAL_TYPES.INSURANCE;

	const close = () => {
		trackInteractionEvent({
			action: "PopupClosed",
			category: "CartPopup",
		});

		props.closePopup();

		if (insurance.length) {
			dispatch(
				toggleModal({
					id: insuranceModalType,
					isOpen: true,
				}),
			);
		}
	};

	const continueShopping = () => {
		trackInteractionEvent({
			action: "ClickedContinueShopping",
			category: "CartPopup",
		});

		props.closePopup();

		if (insurance.length) {
			dispatch(
				toggleModal({
					id: insuranceModalType,
					isOpen: true,
				}),
			);
		}
	};

	const goToCart = () => {
		trackInteractionEvent({
			action: "ClickedGoToCart",
			category: "CartPopup",
		});

		props.closePopup();

		if (insurance.length) {
			dispatch(
				toggleModal({
					id: insuranceModalType,
					isOpen: true,
					config: { redirectUrl: "/cart" },
				}),
			);
		} else {
			window.location.href = "/cart";
		}
	};

	useEffect(() => {
		if (drawerRef.current) {
			drawerRef.current.focus();
		}
	}, []);

	return (
		<div className="addtocart-popup__content" ref={drawerRef} tabIndex={0}>
			<Header closePopup={close} />
			<div
				className={classNames({
					"addtocart-main": true,
					"addtocart-main--enlarge": !isFetching,
					"addtocart-main--regular": isFetching,
				})}
			>
				{isFetching && <Loading label={strings.loading} />}

				{isError ? (
					<>
						{showSubscriptionDataOnly ? (
							<>
								<SubscriptionHighlight isInfoModal />
								<Spacer variant="medium" />
								<Alert />
							</>
						) : (
							<>
								<Alert />
								<Footer
									continueShopping={continueShopping}
									goToCart={goToCart}
								/>
							</>
						)}
					</>
				) : (
					<div>
						{showSubscriptionDataOnly ? (
							<SubscriptionInfo />
						) : (
							<div>
								<BoughtProduct />
								<ConfigServices />

								{isBoughtWithSubscription && <SubscriptionDetails />}
								<Footer
									continueShopping={continueShopping}
									goToCart={goToCart}
								/>
								{isBoughtWithSubscription && (
									<>
										<SubscriptionHighlight />
										<SubscriptionReadMore />
									</>
								)}
								<ProductsList />
								<Configuration />
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};
